import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Building2, MapPin, Save, RefreshCcw, TrendingUp, Map } from "lucide-react";

/**
 * Multi-warehouse admin tab.
 *
 * Sections:
 *  1. Warehouse roster — inline-edit name / status / origin address per warehouse.
 *  2. Analytics — velocity per hub + regional demand heatmap.
 *  3. State → warehouse routing table.
 */
export default function AdminWarehousesTab() {
  const [warehouses, setWarehouses] = useState([]);
  const [routing, setRouting] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, r, a] = await Promise.all([
        api.get("/admin/warehouses"),
        api.get("/admin/warehouse-routing"),
        api.get("/admin/warehouses/analytics").catch(() => ({ data: null })),
      ]);
      setWarehouses(w.data || []);
      setRouting(r.data || []);
      setAnalytics(a.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStateRoute = async (stateCode, warehouseId) => {
    await api.patch(`/admin/warehouse-routing/${stateCode}`, { warehouse_id: warehouseId });
    setRouting(rs => rs.map(r => r.state_code === stateCode ? { ...r, warehouse_id: warehouseId, source: "admin_override" } : r));
  };

  if (loading) return <p data-testid="wh-admin-loading" className="text-[var(--ff-text-muted)]">Loading…</p>;

  return (
    <div className="space-y-10" data-testid="admin-warehouses-tab">
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={20}/>
          <h2 className="font-display text-2xl">Warehouses</h2>
          <button onClick={load} className="ml-auto text-xs inline-flex items-center gap-1 text-[var(--ff-primary)] hover:underline" data-testid="wh-admin-refresh">
            <RefreshCcw size={12}/> Refresh
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {warehouses.map(w => (
            <WarehouseCard key={w.id} warehouse={w} onSaved={load}/>
          ))}
        </div>
      </section>

      {analytics && <AnalyticsSection analytics={analytics}/>}

      <section>
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={20}/>
          <h2 className="font-display text-2xl">State → warehouse routing</h2>
          <span className="text-xs text-[var(--ff-text-muted)] ml-3">
            {routing.filter(r => r.source === "admin_override").length} overrides · {routing.length} states total
          </span>
        </div>
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">State</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Fulfilled by</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider">Source</th>
              </tr>
            </thead>
            <tbody>
              {routing.map(r => (
                <tr key={r.state_code} className="border-t border-stone-200 hover:bg-stone-50" data-testid={`routing-row-${r.state_code}`}>
                  <td className="px-4 py-2 font-mono">{r.state_code}</td>
                  <td className="px-4 py-2">
                    <select
                      value={r.warehouse_id}
                      onChange={e => setStateRoute(r.state_code, e.target.value)}
                      className="input-base text-sm py-1"
                      data-testid={`routing-select-${r.state_code}`}
                    >
                      {warehouses.map(w => (
                        <option key={w.id} value={w.id}>{w.name.replace(" Fulfillment Center", "")} ({w.origin_address?.state})</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.source === "admin_override" ? (
                      <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-semibold">override</span>
                    ) : (
                      <span className="text-[var(--ff-text-muted)] text-[10px]">seed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function WarehouseCard({ warehouse, onSaved }) {
  const [form, setForm] = useState({
    name: warehouse.name,
    status: warehouse.status,
    origin: { ...(warehouse.origin_address || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = (patch) => { setForm(f => ({ ...f, ...patch })); setDirty(true); };
  const setAddr = (patch) => { setForm(f => ({ ...f, origin: { ...f.origin, ...patch } })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/warehouses/${warehouse.id}`, {
        name: form.name,
        status: form.status,
        origin_address: form.origin,
      });
      setDirty(false);
      onSaved?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="card-flat p-5" data-testid={`wh-card-${warehouse.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5">
          {warehouse.id.toUpperCase()}
        </span>
        {warehouse.is_default && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">default</span>
        )}
        <select value={form.status} onChange={e => set({ status: e.target.value })} className="ml-auto text-xs bg-transparent border-0 focus:ring-0 font-semibold">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <input
        className="input-base w-full font-display text-xl"
        value={form.name}
        onChange={e => set({ name: e.target.value })}
        data-testid={`wh-name-${warehouse.id}`}
      />
      <div className="grid grid-cols-2 gap-2 mt-3">
        <input placeholder="Street" className="input-base text-sm col-span-2" value={form.origin.street1 || ""} onChange={e => setAddr({ street1: e.target.value })}/>
        <input placeholder="City" className="input-base text-sm" value={form.origin.city || ""} onChange={e => setAddr({ city: e.target.value })}/>
        <input placeholder="State" className="input-base text-sm" value={form.origin.state || ""} onChange={e => setAddr({ state: e.target.value.toUpperCase().slice(0, 3) })} maxLength={3}/>
        <input placeholder="ZIP" className="input-base text-sm" value={form.origin.zip || ""} onChange={e => setAddr({ zip: e.target.value })}/>
        <input placeholder="Phone" className="input-base text-sm" value={form.origin.phone || ""} onChange={e => setAddr({ phone: e.target.value })}/>
      </div>
      <div className="flex items-center justify-end mt-3">
        <button onClick={save} disabled={!dirty || saving}
          className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
          data-testid={`wh-save-${warehouse.id}`}>
          <Save size={12}/> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}


function AnalyticsSection({ analytics }) {
  const velocity = analytics?.velocity || {};
  const heatmap = (analytics?.demand_heatmap || []).slice(0, 12);
  const totals = analytics?.totals || {};
  const maxOrders = heatmap[0]?.orders_30d || 1;

  return (
    <section data-testid="wh-analytics-section">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={20}/>
        <h2 className="font-display text-2xl">Analytics</h2>
        <span className="text-xs text-[var(--ff-text-muted)] ml-3">
          {totals.orders_30d ?? 0} orders · last 30 days · {totals.warehouses_active ?? 0} active warehouses
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Velocity per hub */}
        <div className="card-flat p-5" data-testid="wh-velocity-card">
          <p className="overline mb-3">Fulfillment velocity — paid → shipped</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[var(--ff-text-muted)]">
                <tr>
                  <th className="text-left py-1.5 text-xs uppercase tracking-wider">Warehouse</th>
                  <th className="text-right py-1.5 text-xs uppercase tracking-wider">7 d</th>
                  <th className="text-right py-1.5 text-xs uppercase tracking-wider">30 d</th>
                  <th className="text-right py-1.5 text-xs uppercase tracking-wider">90 d</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(velocity).map(([whId, v]) => (
                  <tr key={whId} className="border-t border-stone-100" data-testid={`wh-velocity-${whId}`}>
                    <td className="py-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5 mr-2">
                        {whId.replace("wh_", "").toUpperCase()}
                      </span>
                      <span className="text-xs text-[var(--ff-text-secondary)]">
                        {(v.warehouse_name || "").replace(" Fulfillment Center", "")}
                      </span>
                    </td>
                    <VelocityCell w={v.window_7d}/>
                    <VelocityCell w={v.window_30d}/>
                    <VelocityCell w={v.window_90d}/>
                  </tr>
                ))}
                {Object.keys(velocity).length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-xs text-[var(--ff-text-muted)]">No shipping activity in the last 90 days.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ff-text-muted)] mt-3">
            Median hours from `paid` to `labeled/shipped`. Empty cells mean no completed shipments in that window.
          </p>
        </div>

        {/* Regional demand heatmap */}
        <div className="card-flat p-5" data-testid="wh-heatmap-card">
          <p className="overline mb-3 inline-flex items-center gap-1"><Map size={12}/> Demand heatmap — top 12 states, 30 days</p>
          {heatmap.length === 0 ? (
            <p className="text-xs text-[var(--ff-text-muted)] py-6 text-center">
              No paid orders in the last 30 days yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {heatmap.map((row) => {
                const pct = Math.round((row.orders_30d / maxOrders) * 100);
                return (
                  <li key={row.state} className="text-sm" data-testid={`heatmap-row-${row.state}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono w-8 text-xs">{row.state}</span>
                      <div className="flex-1 h-6 bg-stone-100 rounded relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[var(--ff-primary)] to-orange-300"
                          style={{ width: `${pct}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold tabular-nums">
                          {row.orders_30d} <span className="text-[var(--ff-text-muted)] ml-2 text-[10px] uppercase">→ {row.top_warehouse.replace("wh_", "")}</span>
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[10px] text-[var(--ff-text-muted)] mt-3">
            Highest-volume states in the last 30 days. Arrow shows which warehouse currently fulfills that state.
          </p>
        </div>
      </div>
    </section>
  );
}

function VelocityCell({ w }) {
  const hrs = w?.median_hours;
  const count = w?.shipped ?? 0;
  return (
    <td className="text-right py-2 tabular-nums">
      {hrs != null ? (
        <>
          <span className="font-semibold">{hrs.toFixed(1)}h</span>
          <span className="text-[10px] text-[var(--ff-text-muted)] ml-1">n={count}</span>
        </>
      ) : (
        <span className="text-[var(--ff-text-muted)] text-xs">—</span>
      )}
    </td>
  );
}

